/**
 * Chat-plane folding (CORD-03 §3): decode stream events into a timeline,
 * enforcing the channel/epoch binding, applying edits, deletes, and
 * reactions, and dropping banned authors.
 */

import { KIND_CHAT_MESSAGE, KIND_DELETE, KIND_EDIT, KIND_REACTION } from "./config";
import { rumorTimeMs } from "./stream";
import type { ChatMessage, DecodedStreamEvent } from "./types";

interface RawRumor {
  id: string;
  kind: number;
  pubkey: string;
  content: string;
  tags: string[][];
  atMs: number;
}

export interface ChatTimeline {
  messages: ChatMessage[];
}

function bindOk(rumor: DecodedStreamEvent["rumor"], channelId: string, epoch: number): boolean {
  const ch = rumor.tags.find(([n]) => n === "channel")?.[1];
  const ep = rumor.tags.find(([n]) => n === "epoch")?.[1];
  // CORD-03 §3: both must strict-equal the key that opened the wrap.
  return ch === channelId && ep === String(epoch);
}

export function foldChatEvents(
  events: DecodedStreamEvent[],
  opts: {
    channelId: string;
    epoch: number;
    myPub?: string;
    banlist: Set<string>;
  },
): ChatTimeline {
  const { channelId, epoch, myPub, banlist } = opts;

  const raw: RawRumor[] = [];
  for (const d of events) {
    const { rumor } = d;
    if (banlist.has(rumor.pubkey)) continue;
    if (!bindOk(rumor, channelId, epoch)) continue;
    const atMs = rumorTimeMs(rumor);
    if (atMs === null) continue;
    raw.push({
      id: rumor.id,
      kind: rumor.kind,
      pubkey: rumor.pubkey,
      content: rumor.content,
      tags: rumor.tags,
      atMs,
    });
  }
  raw.sort((a, b) => a.atMs - b.atMs || (a.id < b.id ? -1 : 1));

  const deleted = new Set<string>();
  for (const r of raw) {
    if (r.kind !== KIND_DELETE) continue;
    for (const t of r.tags) {
      // A delete only ever applies to its author's own rumors (CORD-02 §9).
      if (t[0] === "e" && typeof t[1] === "string") deleted.add(`${r.pubkey}:${t[1]}`);
    }
  }

  const edits = new Map<string, { content: string; atMs: number }>();
  for (const r of raw) {
    if (r.kind !== KIND_EDIT) continue;
    const target = r.tags.find(([n]) => n === "e")?.[1];
    if (!target) continue;
    const key = `${r.pubkey}:${target}`;
    const prev = edits.get(key);
    if (!prev || r.atMs > prev.atMs) edits.set(key, { content: r.content, atMs: r.atMs });
  }

  const reactions = new Map<string, Map<string, { count: number; mine: boolean; ids: string[] }>>();
  for (const r of raw) {
    if (r.kind !== KIND_REACTION) continue;
    const target = r.tags.find(([n]) => n === "e")?.[1];
    if (!target || !r.content) continue;
    const emoji = r.content === "+" ? "❤️" : r.content;
    let perMsg = reactions.get(target);
    if (!perMsg) reactions.set(target, (perMsg = new Map()));
    const cur = perMsg.get(emoji) ?? { count: 0, mine: false, ids: [] };
    cur.count++;
    cur.ids.push(r.id);
    if (r.pubkey === myPub) cur.mine = true;
    perMsg.set(emoji, cur);
  }

  const messages: ChatMessage[] = [];
  for (const r of raw) {
    if (r.kind !== KIND_CHAT_MESSAGE) continue;
    if (deleted.has(`${r.pubkey}:${r.id}`)) continue;
    const edit = edits.get(`${r.pubkey}:${r.id}`);
    const perMsg = reactions.get(r.id);
    messages.push({
      id: r.id,
      pubkey: r.pubkey,
      content: edit?.content ?? r.content,
      createdAtMs: r.atMs,
      reactions: perMsg ? Object.fromEntries(perMsg) : {},
      edited: edit !== undefined,
    });
  }
  return { messages };
}
